import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { customerLogin, type ErrorResponse } from '@vn-propane/shared';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Label } from '@/components/ui/label';
import { LanguageSelector } from '@/components/ui/language-selector';
import { LegalFooter } from '@vn-propane/shared/components';
import { CircleHelp } from 'lucide-react';

export default function LoginPage() {
  const { t, i18n } = useTranslation(['auth', 'common', 'errors']);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Get token and order_id from URL (passed from register page for existing users)
  const token = searchParams.get('token');
  const orderId = searchParams.get('order_id');

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      const result = await customerLogin({
        body: {
          identifier: username,
          password,
          // Pass token to link customer_user to this customer profile
          token: token || undefined,
        },
      });

      if (result.data) {
        // Redirect to order if we have order_id, otherwise to home
        if (orderId) {
          navigate(`/orders/${orderId}`);
        } else {
          navigate('/home');
        }
        return;
      }

      if (result.error) {
        const errorResponse = result.error as ErrorResponse;
        setError(errorResponse.error?.message || t('validation.loginFailed'));
      } else {
        setError(t('validation.loginFailed'));
      }
    } catch {
      setError(t('errors:unexpectedError'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-muted/40 relative">
      <div className="absolute top-4 right-4 flex items-center gap-2">
        <a
          href={i18n.language === 'en' ? `${import.meta.env.VITE_HELP_URL || '/help'}/en/customer/` : `${import.meta.env.VITE_HELP_URL || '/help'}/customer/`}
          target="_blank"
          rel="noopener noreferrer"
          className="p-2 rounded-md hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
          title={t('common:navigation.help')}
        >
          <CircleHelp className="h-5 w-5" />
        </a>
        <LanguageSelector />
      </div>
      <div className="w-full max-w-md p-8 bg-background rounded-sm border shadow-sm">
        <div className="flex flex-col items-center mb-6">
          <img src={`${import.meta.env.BASE_URL}logo-mark-dark.png`} alt="Giao Gas" className="h-14 w-14 mb-3" />
          <h1 className="text-2xl font-extrabold tracking-tight text-center">
            giao<span className="text-propane-500">gas</span>
          </h1>
          <p className="text-muted-foreground text-sm text-center mt-1">
            {t('customer.loginDescription')}
          </p>
        </div>
        {error && (
          <Alert variant="destructive" className="mb-4">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="username">{t('fields.username')}</Label>
            <Input
              id="username"
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
              disabled={isLoading}
              autoComplete="username"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="password">{t('fields.password')}</Label>
            <Input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              disabled={isLoading}
              autoComplete="current-password"
            />
          </div>
          <Button type="submit" disabled={isLoading} className="w-full">
            {isLoading ? t('signingIn') : t('signIn')}
          </Button>
        </form>
        <p className="text-sm text-center mt-4 text-muted-foreground">
          {t('links.dontHaveAccount')}{' '}
          <Link
            to={`/register${token ? `?token=${encodeURIComponent(token)}${orderId ? `&order_id=${encodeURIComponent(orderId)}` : ''}` : ''}`}
            className="text-primary hover:underline"
          >
            {t('register')}
          </Link>
        </p>
      </div>
      <LegalFooter className="absolute bottom-6" />
    </div>
  );
}
